import styled from 'styled-components'
import { memo, Suspense, useCallback, useEffect } from 'react'
import { Column } from 'react-virtualized/dist/es/Table'
import { SortDirection } from 'react-virtualized/dist/commonjs/Table'
import { VirtualizedTable } from './VirtualizedTable'
import { Icon } from '@fluentui/react/lib/Icon'
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer'
import { graphEventHelper, graphResourceHelper } from '../../common/src/helpers'
import { ITEMS_SORT_TYPE } from '../../common/src/actions/filtersActions'
import { getGenericSortPredicateForGraphResources } from '../../common/src/helpers/itemsFiltersHelper'
import { Loading } from '../basic/Loading'
import { useMergeState } from '../../common/src/hooks/enhancedHooks'
import { useTranslation } from 'react-i18next'
import { LazyIViewPlaceholder } from '../placeholder/LazyIViewPlaceholder'
import { toDate } from '../../common/src/helpers/dateHelper'
import { formatDate } from '../../utils/datefns'

const IMAGE_SIZE = 24

const SIcon = styled(Icon).attrs({
  side: IMAGE_SIZE,
})`
  font-size: ${IMAGE_SIZE - 6}px;
  margin: auto;
`

const Header = styled.div`
  color: ${p => p.theme.palette.black};
  font-weight: 600;
  text-transform: none;
`

const Sort = styled(Icon).attrs(props => ({
  iconName: props.sortDirection === SortDirection.DESC ? 'SortDown' : 'SortUp',
}))`
  font-size: 10px;
  margin-left: 2px;
`

const Text = styled.div`
  color: ${p => p.theme.palette.black};
`

const Line = styled(Shimmer).attrs({
  shimmerElements: [
    {
      type: ShimmerElementType.line,
      height: 12,
    },
  ],
})`
  flex: 1;
`

const headerStyle = {
  textAlign: 'center',
}

const columnStyle = {
  ...headerStyle,
}

export const GRAPH_ITEM_COLUMN_KEYS = {
  ICON: ITEMS_SORT_TYPE.ICON,
  NAME: ITEMS_SORT_TYPE.NAME,
  LAST_MODIFIED_TIMESTAMP: ITEMS_SORT_TYPE.LAST_MODIFIED_TIMESTAMP,
}

const sortItems = (array, sortBy, sortDirection) => {
  const isAscending = sortDirection === SortDirection.ASC
  const predicate = getGenericSortPredicateForGraphResources(sortBy, isAscending)
  return [...array].sort(predicate)
}

const defaultCellDataGetter = ({ rowData }) => rowData

const initialState = {
  sortedItems: [],
  sortBy: null,
  sortDirection: SortDirection.DESC,
}

export const GraphItemVirtualizedTable = memo(
  ({
    items,
    selectedItem,
    sortBy: defaultSortBy = GRAPH_ITEM_COLUMN_KEYS.LAST_MODIFIED_TIMESTAMP,
    isSortEnabled = false,
    placeholder,
    ...rest
  }) => {
    const [state, setState] = useMergeState({ ...initialState, sortBy: defaultSortBy })
    const { sortedItems, sortBy, sortDirection } = state
    const { t } = useTranslation()

    useEffect(() => {
      if (isSortEnabled && sortBy) {
        const newSortedItems = sortItems(items, sortBy, sortDirection)
        setState({ sortedItems: newSortedItems })
      } else {
        setState({ sortedItems: null })
      }
    }, [isSortEnabled, items, sortBy, sortDirection, setState])

    const headerRenderer = useCallback(
      ({ dataKey, sortBy, sortDirection, label }) => {
        let main
        switch (dataKey) {
          case GRAPH_ITEM_COLUMN_KEYS.ICON:
            main = <Icon iconName="IconSetsFlag" />
            break
          default:
            main = label
            break
        }
        return (
          <Header>
            {main}
            {isSortEnabled && sortBy === dataKey && <Sort sortDirection={sortDirection} />}
          </Header>
        )
      },
      [isSortEnabled]
    )

    const iconRenderer = useCallback(({ rowData, ...rest }) => {
      if (!rowData) {
        return <Line />
      }
      const { icon, iconColor } = rowData
      if (!icon) {
        return false
      }
      return <SIcon iconName={icon} style={{ color: iconColor }} />
    }, [])

    const nameRenderer = useCallback(({ rowData }) => {
      if (!rowData) {
        return <Line />
      }
      const { name } = rowData
      return <Text>{name}</Text>
    }, [])

    const lastModifiedDateGetter = useCallback(({ rowData }) => {
      if (!rowData) {
        return rowData
      }
      return graphResourceHelper.getLastModifiedTimestampDate(rowData)
    }, [])

    const dateRenderer = useCallback(({ rowData: graphItem, cellData: dateModified }) => {
      if (!graphItem) {
        return <Line />
      }
      if (!dateModified) {
        return false
      }
      const date = graphEventHelper.isEvent(graphItem) ? graphEventHelper.getStartDate(graphItem) : toDate(dateModified)
      const text = formatDate('ShortenedAllDay')(date)
      return <Text>{text}</Text>
    }, [])

    const sort = useCallback(
      ({ sortBy, sortDirection }) => {
        setState({ sortBy, sortDirection })
      },
      [setState]
    )

    const noRowsRenderer = useCallback(() => {
      return (
        <Suspense fallback={<Loading />}>
          <LazyIViewPlaceholder {...placeholder} />
        </Suspense>
      )
    }, [placeholder])

    const rows = sortedItems || items
    return (
      <VirtualizedTable
        rows={rows}
        selectedRow={selectedItem}
        noRowsRenderer={noRowsRenderer}
        sortBy={sortBy}
        sortDirection={sortDirection}
        sort={sort}
        {...rest}
      >
        <Column
          label={t('item.icon.title')}
          dataKey={GRAPH_ITEM_COLUMN_KEYS.ICON}
          headerRenderer={headerRenderer}
          cellDataGetter={defaultCellDataGetter}
          cellRenderer={iconRenderer}
          width={30}
          disableSort
          headerStyle={headerStyle}
          style={columnStyle}
        />
        <Column
          label={t('item.name')}
          dataKey={GRAPH_ITEM_COLUMN_KEYS.NAME}
          headerRenderer={headerRenderer}
          cellDataGetter={defaultCellDataGetter}
          cellRenderer={nameRenderer}
          flexGrow={1}
          width={200}
          disableSort={!isSortEnabled}
        />
        <Column
          label={t('date.date')}
          dataKey={GRAPH_ITEM_COLUMN_KEYS.LAST_MODIFIED_TIMESTAMP}
          headerRenderer={headerRenderer}
          cellDataGetter={lastModifiedDateGetter}
          cellRenderer={dateRenderer}
          width={80}
          disableSort={!isSortEnabled}
          headerStyle={headerStyle}
          style={columnStyle}
        />
      </VirtualizedTable>
    )
  }
)
